import { LanguageToggleComponent } from 'app/shared/components/language-toggle/language-toggle.component';
import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { HashMap, TranslocoService } from '@ngneat/transloco';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { AnilloPerfilComponent } from 'app/shared/components/anillo-perfil/anillo-perfil.component';
import { FeedbackDialogComponent } from 'app/shared/components/feedback-dialog/feedback-dialog.component';
import { PerfilCompletoService } from 'app/core/user/perfil-completo.service';
import { ConTermino, forma } from 'app/core/user/termino';
import { TerminoService } from 'app/core/user/termino.service';
import { DashboardService } from 'app/shared/services/dashboard.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { InboundContador, InboundReceiptsService } from 'app/shared/services/inbound-receipts.service';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        LanguageToggleComponent,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        RouterOutlet,
        QuickChatComponent,
        AnilloPerfilComponent,
        AsyncPipe,
    ],
})
// --no-warn-ignored
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    private _termino = inject(TerminoService);
    /** Anillo de "perfil completo" alrededor del avatar del menú. */
    readonly progresoPerfil$ = inject(PerfilCompletoService).progreso$;
    private _transloco = inject(TranslocoService);
    private _inbound = inject(InboundReceiptsService);
    /** Contador de comprobantes por WhatsApp; `null`/`undefined` = el back no tiene la bandeja. */
    private _comprobantes: InboundContador | null | undefined;
    private _dashboard = inject(DashboardService);
    private _snackBar = inject(MatSnackBar);
    /**
     * Si la persona participa en alguna tanda (no cancelada) de otro
     * organizador. Sin participaciones, "Mis participaciones" no se enseña.
     * Arranca con lo último que se supo (sin parpadeo) y un error de red nunca
     * la oculta: se conserva el último valor.
     */
    private _tieneParticipaciones = false;
    /** Usuario para el que ya se preguntó (el perfil se emite varias veces). */
    private _participacionesDe: string | null = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _dialog: MatDialog,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        // Subscribe to navigation data
        // this._navigationService.navigation$
        //     .pipe(takeUntil(this._unsubscribeAll))
        //     .subscribe((navigation: Navigation) => {
        //         this.navigation = navigation;
        //     });
        this._construirMenu();

        // Load real user data from backend
        this._userService.get().subscribe({
            next: (user) => {
                this.user = user;
                this._construirMenu();
                this._aplicarCandadoPro(user);
            },
            error: () => {
                // Fallback if API fails
                const fallback: User = { id: '0', name: this._transloco.translate('navigation.fallbackUser'), email: '', status: 'online' };
                this.user = fallback;
                this._userService.user = fallback;
            }
        });

        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                // El menu nombra la tanda con la palabra del usuario; se rearma
                // cada vez que llega el perfil o la cambia en Preferencias.
                this._construirMenu();
                this._aplicarCandadoPro(user);
                this._revisarParticipaciones(user);
            });

        // Bandeja de comprobantes por WhatsApp: sólo aparece en el menú si el
        // back la tiene (un 404 deja el contador en null y la opción oculta).
        this._inbound.contador$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((c) => {
                this._comprobantes = c;
                this._aplicarComprobantes();
            });
        this._inbound.refrescarContador().subscribe();

        // Pantalla chica (menos de md): el menú va encima del contenido y
        // cerrado; abierto de lado dejaba ~60 px para la página en un teléfono.
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * Texto del menu en el idioma activo, con las formas del termino como
     * parametros. Cambiar de idioma recarga la app, asi que basta con
     * traducir al armar el menu.
     */
    private _tr(llave: string): string {
        const t = this._termino;
        const params: HashMap = {
            termino: t.termino(),
            Plural: t.Plural(),
            plural: t.plural(),
            tus: t.f('tus'),
            una: t.f('una'),
            nueva: t.f('nueva'),
        };
        return this._transloco.translate(`navigation.${llave}`, params);
    }

    /** `_f`/`_m` segun el genero del termino; en ingles las dos dicen lo mismo. */
    private _trG(llave: string): string {
        return this._tr(`${llave}_${this._termino.masculino() ? 'm' : 'f'}`);
    }

    /** Arma el menu lateral con el termino del usuario ("tanda", "cundina", "san"...). */
    private _construirMenu(): void {
        this.navigation = {
            default: [
                {
                    id: 'divider-1',
                    type: 'divider'
                },
                {
                    id: 'dashboard',
                    type: 'basic',
                    title: this._tr('dashboard'),
                    tooltip: this._tr('dashboardTooltip'),
                    icon: 'dashboard',
                    link: '/tandaplus/dashboard'
                },
                {
                    id: 'comprobantes',
                    type: 'basic',
                    title: this._tr('receipts'),
                    tooltip: this._tr('receiptsTooltip'),
                    icon: 'fact_check',
                    link: '/tandaplus/comprobantes',
                    hidden: () => !this._comprobantes,
                    badge: this._badgeComprobantes(),
                },
                {
                    id: 'crear-tanda',
                    type: 'basic',
                    title: this._tr('create'),
                    tooltip: this._trG('createTooltip'),
                    icon: 'add',
                    link: '/tandaplus/nueva-tanda'
                },
                {
                    id: 'contactos',
                    type: 'basic',
                    title: this._tr('contacts'),
                    tooltip: this._tr('contactsTooltip'),
                    icon: 'people',
                    link: '/tandaplus/contactos'
                },
                {
                    // El estado bloqueado/desbloqueado lo ajusta _aplicarCandadoPro()
                    // cuando llega el usuario; aquí arranca bloqueado a propósito,
                    // para que un usuario básico nunca lo vea abierto ni por un instante.
                    id: 'articulos',
                    type: 'basic',
                    title: this._tr('articles'),
                    tooltip: this._tr('articlesTooltip'),
                    icon: 'redeem',
                    link: '/tandaplus/articulos',
                    disabled: true,
                    badge: { title: 'PRO', classes: 'px-2 bg-indigo-100 text-indigo-600 rounded-full' },
                },
                {
                    id: 'historial-tandas',
                    type: 'basic',
                    title: this._tr('history'),
                    tooltip: this._trG('historyTooltip'),
                    icon: 'history',
                    link: '/tandaplus/historial-tandas'
                },
                {
                    id: 'mis-participaciones',
                    type: 'basic',
                    title: this._tr('participations'),
                    tooltip: this._tr('participationsTooltip'),
                    icon: 'group_work',
                    link: '/tandaplus/mis-participaciones',
                    hidden: () => !this._tieneParticipaciones,
                },
                {
                    id: 'divider-marca',
                    type: 'divider',
                },
                {
                    // Mi marca va aparte, en su propio bloque del menú: es lo que el
                    // organizador enseña a sus clientes, no una opción de su perfil.
                    // Abierta para todos (sin PRO se prueba y se ve la vista previa;
                    // sólo guardar pide PRO). La insignia lo dice.
                    id: 'mi-marca',
                    type: 'basic',
                    title: this._tr('myBrand'),
                    tooltip: this._tr('myBrandTooltip'),
                    icon: 'palette',
                    link: '/tandaplus/mi-marca',
                    badge: { title: 'PRO', classes: 'px-2 bg-indigo-100 text-indigo-600 rounded-full' },
                },
                {
                    id: 'divider-feedback',
                    type: 'divider',
                },
                {
                    id: 'novedades',
                    type: 'basic',
                    title: this._tr('whatsNew'),
                    tooltip: this._tr('whatsNewTooltip'),
                    icon: 'new_releases',
                    link: '/tandaplus/novedades'
                },
                {
                    id: 'feedback',
                    type: 'basic',
                    title: this._tr('feedback'),
                    tooltip: this._tr('feedbackTooltip'),
                    icon: 'feedback',
                    function: () => this.openFeedback(),
                },
                /*
                {
                    id: 'divider-2',
                    type: 'divider',
                },
                {
                    type: 'collapsable',
                    title: 'collapsable',
                    children: [
                        {
                            type: 'basic',
                            title: 'Opcion 1',
                        },
                        {
                            type: 'basic',
                            title: 'Opcion 2',
                        },

                    ]
                },
                {
                    type: 'divider',
                },
                {
                    type: 'group',
                    title: 'Grupo de opciones',
                },
                {
                    type: 'basic',
                    title: 'Opcion 1',
                },
                {
                    id: 'aside-btn-1',
                    type: 'aside',
                    title: 'Aside option',
                    children: [
                        {
                            type: 'basic',
                            title: 'Opcion 1',
                        },
                        {
                            type: 'basic',
                            title: 'Opcion 2',
                        },
                    ]
                },
                */
            ],
            compact: [],
            futuristic: [],
            horizontal: []
        };
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Abre o bloquea "Mis artículos" según el plan del usuario.
     *
     * El catálogo de artículos es una función Premium: al plan básico se le
     * muestra la opción —para que sepa que existe— pero deshabilitada y con la
     * insignia PRO, en vez de ocultarla. Si se ocultara, nadie descubriría la
     * función; si se dejara abierta, el usuario llegaría a una pantalla donde
     * todo le falla.
     *
     * Se reasigna `navigation` (no se muta en su lugar) porque el componente
     * corre con OnPush y una mutación no dispararía el redibujado del menú.
     */
    private _aplicarCandadoPro(user: User): void {
        if (!this.navigation?.default) {
            return;
        }

        const esPro = !!user?.opc_is_pro;

        this.navigation = {
            ...this.navigation,
            default: this.navigation.default.map((item) =>
                item.id !== 'articulos'
                    ? item
                    : {
                          ...item,
                          disabled: !esPro,
                          tooltip: esPro
                              ? this._tr('articlesTooltip')
                              : this._tr('articlesLocked'),
                          badge: esPro
                              ? undefined
                              : {
                                    title: 'PRO',
                                    classes:
                                        'px-2 bg-indigo-100 text-indigo-600 rounded-full',
                                },
                      },
            ),
        };
    }

    private _llaveParticipaciones(idUsuario: string): string {
        return `tp.tiene_participaciones.${idUsuario}`;
    }

    /** Lo último que se supo en este navegador: 'true', 'false' o null (nunca se preguntó). */
    private _leerParticipaciones(idUsuario: string): string | null {
        try {
            return localStorage.getItem(this._llaveParticipaciones(idUsuario));
        } catch {
            return null;
        }
    }

    private _guardarParticipaciones(idUsuario: string, valor: boolean): void {
        try {
            localStorage.setItem(this._llaveParticipaciones(idUsuario), String(valor));
        } catch {
            // Sin almacenamiento (modo privado): solo se pierde el arranque sin parpadeo.
        }
    }

    /**
     * Muestra "Mis participaciones" solo si participa en alguna tanda. Se pinta
     * primero con lo guardado y luego se confirma con el back; si la consulta
     * falla, se queda como estaba. La primera vez que aparece, se avisa.
     */
    private _revisarParticipaciones(user: User): void {
        const id = user?.id;
        if (!id || id === '0' || this._participacionesDe === id) return;
        this._participacionesDe = id;
        const previo = this._leerParticipaciones(id);
        this._ponerParticipaciones(previo === 'true');
        this._dashboard.getMyParticipations().subscribe({
            next: (data) => {
                const tandas = ((data as { tandas?: unknown[] })?.tandas ?? []) as (ConTermino & { name_organizer?: string })[];
                const tiene = tandas.length > 0;
                this._guardarParticipaciones(id, tiene);
                this._ponerParticipaciones(tiene);
                // Llegó la primera: antes se sabía que no participaba en nada.
                if (tiene && previo === 'false') this._avisarPrimeraParticipacion(tandas[0]);
            },
            error: () => {
                // Nunca ocultar por un error de red: se conserva lo último.
            },
        });
    }

    private _ponerParticipaciones(valor: boolean): void {
        if (this._tieneParticipaciones === valor) return;
        this._tieneParticipaciones = valor;
        if (!this.navigation?.default) return;
        // Se reasigna para que el menú (OnPush) vuelva a evaluar `hidden`.
        this.navigation = { ...this.navigation, default: [...this.navigation.default] };
    }

    private _avisarPrimeraParticipacion(tanda: ConTermino & { name_organizer?: string }): void {
        const seccion = this._tr('participations');
        const msg = tanda?.name_organizer
            ? this._transloco.translate('navigation.firstParticipation', {
                  la: forma(tanda, 'la'),
                  organizador: tanda.name_organizer,
                  seccion,
              })
            : this._transloco.translate('navigation.firstParticipationNoName', { seccion });
        this._snackBar
            .open(msg, this._transloco.translate('navigation.seeIt'), { duration: 8000 })
            .onAction()
            .subscribe(() => this._router.navigate(['/tandaplus/mis-participaciones']));
    }

    private _badgeComprobantes(): { title: string; classes: string } | undefined {
        const n = (this._comprobantes?.por_revisar ?? 0) + (this._comprobantes?.por_asignar ?? 0);
        return n > 0
            ? { title: n > 99 ? '99+' : String(n), classes: 'px-2 bg-green-600 text-white rounded-full' }
            : undefined;
    }

    /** Muestra u oculta "Comprobantes" y pone su contador (reasigna por OnPush). */
    private _aplicarComprobantes(): void {
        if (!this.navigation?.default) return;
        this.navigation = {
            ...this.navigation,
            default: this.navigation.default.map((item) =>
                item.id !== 'comprobantes'
                    ? item
                    : { ...item, hidden: () => !this._comprobantes, badge: this._badgeComprobantes() },
            ),
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    openFeedback(): void {
        this._dialog.open(FeedbackDialogComponent, {
            width: '500px',
            maxWidth: '95vw',
        });
    }

    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
